import React from 'react';

const Features: React.FC = () => {
    const values = [
      { icon: "/layout/shield.svg", title: "Stay in control", description: "Candidates remain anonymous until they decide to engage with a company.", detail: "", size: "small" },
      { icon: "/layout/guide.svg", title: "Clear at every step", description: "Both candidates and companies have visibility from end to end.", detail: "No hidden agendas, not driven by commission or metrics and no black boxes. Our founding pillars are trust and transparency, ensuring candidates and companies can connect worry-free.", size: "large" },
      { icon: "/layout/idea.svg", title: "Scalable Intelligence", description: "Hush's AI doesn't just match resumes to jobs", detail: "It understands context, refines searches, initiates outreach, and manages scheduling. The result: less manual work, faster placements, and a hiring pipeline that runs itself.", size: "large" },
      { icon: "/layout/chart.svg", title: "Smarter pricing", description: "20-30% fees are history, only pay for qualified candidates.*", detail: "", size: "small" }
    ];

    return (
      <section className="relative pt-4 pb-4 px-[22px] sm:px-4 overflow-x-hidden">
        <div className="container mx-auto max-w-[1600px] relative z-10 sm:px-16">
          {/* Title and Subtitle Section with Background SVG */}
          <div className="mb-4 sm:mb-16 relative min-h-[120px] sm:min-h-[250px] flex items-center justify-center">
            {/* Background SVG - Hide on mobile */}
            <div
              className="hidden sm:block absolute inset-0"
              style={{
                backgroundImage: 'url(/branding/title.svg)',
                backgroundSize: 'contain',
                backgroundPosition: 'center 15%',
                backgroundRepeat: 'no-repeat',
                opacity: 1,
                zIndex: 0,
                filter: 'brightness(1.5)'
              }}
            />

            {/* Content */}
            <div className="relative z-10 text-center py-0 sm:py-[60px]">
              {/* Small Title */}
              <p
                className="text-white mb-4 sm:mb-6 text-[18px] sm:text-[24px]"
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  lineHeight: '100%',
                  letterSpacing: '-0.04em',
                  textAlign: 'center'
                }}
              >
                Recruiting made smarter
              </p>

              {/* Main Heading with Gradient */}
              <h2
                className="text-white text-[24px] leading-[30px] sm:!text-[clamp(32px,5vw,64px)] sm:!leading-[1.3]"
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  maxWidth: '900px',
                  margin: '0 auto'
                }}
              >
                <span style={{ color: '#FFFFFF' }}>Control, transparency, cost-effectiveness, </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.75)' }}>and AI at your side.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`text-left sm:transition-all sm:duration-300 sm:hover:scale-[1.02] ${value.size === 'large' ? 'md:col-span-3' : 'md:col-span-2'}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '24px',
                  minHeight: value.size === 'large' ? '220px' : '200px',
                  background: 'radial-gradient(146.13% 118.42% at 50% -15.5%, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 99.59%), linear-gradient(180deg, rgba(46, 51, 90, 0) 0%, rgba(28, 27, 51, 0.1) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: 'inset 0px 0px 100px rgba(204, 215, 255, 0.15)',
                  borderRadius: '24px',
                  boxSizing: 'border-box',
                  transform: 'translateZ(0)',
                  willChange: 'auto'
                }}
              >
                {value.size === 'small' ? (
                  // Small cards: icon at top, text below
                  <>
                    <div className="mb-4 sm:mb-6 flex-shrink-0">
                      <img
                        src={value.icon}
                        alt={value.title}
                        className="w-[80px] sm:w-[120px] h-auto object-contain block"
                      />
                    </div>
                    <div className="flex flex-col gap-3 sm:gap-4 flex-1">
                      <h3
                        className="text-[20px] sm:text-[24px] leading-[24px] sm:leading-[27px]"
                        style={{
                          fontFamily: 'Inter',
                          fontWeight: 700,
                          letterSpacing: '0%',
                          background: 'linear-gradient(91.35deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 103.32%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          margin: 0
                        }}
                      >
                        {value.title}
                      </h3>
                      <p
                        className="text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px]"
                        style={{
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          letterSpacing: '0%',
                          color: 'rgba(255, 255, 255, 0.8)',
                          margin: 0
                        }}
                      >
                        {value.description}
                      </p>
                    </div>
                  </>
                ) : (
                  // Large cards: title and description at top, then icon with detail text side by side
                  <>
                    <div className="flex flex-col gap-3 sm:gap-4 mb-4">
                      <h3
                        className="text-[20px] sm:text-[24px] leading-[24px] sm:leading-[27px]"
                        style={{
                          fontFamily: 'Inter',
                          fontWeight: 700,
                          letterSpacing: '0%',
                          background: 'linear-gradient(91.35deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 103.32%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          margin: 0
                        }}
                      >
                        {value.title}
                      </h3>
                      <p
                        className="text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px]"
                        style={{
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          letterSpacing: '0%',
                          color: 'rgba(255, 255, 255, 0.8)',
                          margin: 0
                        }}
                      >
                        {value.description}
                      </p>
                    </div>
                    {value.detail && (
                      <div className="flex gap-4 sm:gap-6 items-start">
                        <div className="flex-shrink-0">
                          <img
                            src={value.icon}
                            alt={value.title}
                            className="w-[80px] sm:w-[120px] h-auto object-contain block"
                          />
                        </div>
                        <p
                          className="text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px] text-justify flex-1"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            letterSpacing: '0%',
                            color: 'rgba(255, 255, 255, 0.6)',
                            margin: 0
                          }}
                        >
                          {value.detail}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
};

export default Features;
